import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import {
    GetClassifierDeployedProfilesWithoutLlmClassificationQuery,
    useUpdateLlmClassificationEnabledMutation
} from '../../api/client'

// Derived from the generated query so a schema change here fails to compile
// rather than drifting silently from what the API actually returns.
export type ProfileWithoutLlmClassification =
    GetClassifierDeployedProfilesWithoutLlmClassificationQuery['getClassifierDeployedProfilesWithoutLlmClassification'][number]

type Props = {
    profiles: ProfileWithoutLlmClassification[]
    /**
     * Set when the lookup itself failed. Rendered as its own state, never as
     * "all clear": an empty list means every applied profile can run the prompt,
     * and a failed check must not be allowed to look like that.
     */
    checkError?: string | null
    /** Refetches the list — after a switch is flipped, or to retry a failed lookup. */
    onChanged: () => Promise<unknown>
}

function labelOf(p: ProfileWithoutLlmClassification): string {
    return p.name ?? `Profile #${p.id}`
}

function messageOf(err: unknown): string {
    return err instanceof Error ? err.message : String(err)
}

/**
 * Sits above the "Applied to" card on the classifier page. A classifier prompt
 * only runs for a profile when that profile's LLM classification switch is on,
 * and nothing about applying a prompt says so — so a prompt applied to a
 * switched-off profile silently does nothing. This names every such profile
 * and lets an admin flip the switch from here rather than hunting through
 * each profile's settings page. Renders nothing when there is nothing to fix,
 * which is the normal case: the switch is on by default (EN-467).
 *
 * Deliberately the only warning-coloured (`suspect`) surface on the page: it's
 * the one thing here that means "your configuration isn't doing what you
 * think", so it has to read before the version editor and deployment table.
 */
export default function ClassifierLlmClassificationWarning({ profiles, checkError, onChanged }: Props) {
    const [updateEnabled] = useUpdateLlmClassificationEnabledMutation()
    // Which profile is mid-flip, 'all' for the bulk action, 'retry' for a re-check. One at a time.
    const [busy, setBusy] = useState<number | 'all' | 'retry' | null>(null)
    const [error, setError] = useState<string | null>(null)

    async function refresh(): Promise<string | null> {
        try {
            await onChanged()
            return null
        } catch (err) {
            return `Couldn't refresh the list: ${messageOf(err)}`
        }
    }

    async function retryCheck() {
        setBusy('retry')
        setError(null)
        setError(await refresh())
        setBusy(null)
    }

    async function turnOn(targets: ProfileWithoutLlmClassification[], marker: number | 'all') {
        setBusy(marker)
        setError(null)
        // One call at a time, each caught on its own, so a failure stays
        // attributable to a profile and the ones already flipped are reported
        // as such. The list is short, so speed isn't a concern.
        const failures: string[] = []
        for (const p of targets) {
            try {
                await updateEnabled({ variables: { accountId: p.id, llmClassificationEnabled: true } })
            } catch (err) {
                failures.push(`${labelOf(p)} (${messageOf(err)})`)
            }
        }
        const messages: string[] = []
        if (failures.length > 0) {
            const done = targets.length - failures.length
            messages.push(`Turned on for ${done} of ${targets.length}. Failed for ${failures.join(', ')}.`)
        }
        // Always refetch, even after failures: the DB now reflects whichever
        // flips succeeded, and the list has to show that rather than go stale.
        const refreshError = await refresh()
        if (refreshError) messages.push(refreshError)
        setError(messages.length > 0 ? messages.join(' ') : null)
        setBusy(null)
    }

    const buttonClass =
        'whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50 ' +
        'bg-suspect-600 hover:bg-suspect-700'

    if (checkError) {
        return (
            <div
                role="alert"
                className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-suspect-300 bg-suspect-50 px-4 py-3"
            >
                <div className="flex items-start gap-3">
                    <ExclamationTriangleIcon
                        aria-hidden="true"
                        className="mt-1 h-5 w-5 shrink-0 text-suspect-700"
                    />
                    <div>
                        <p className="text-[13px] font-semibold text-ink-900">
                            Couldn&rsquo;t check whether the applied profiles have LLM classification
                            switched on
                        </p>
                        <p className="mt-0.5 text-xs text-ink-600">
                            The prompt may be applied to profiles it can&rsquo;t run for. {checkError}
                        </p>
                        {error && <p className="mt-1 text-xs text-severe">{error}</p>}
                    </div>
                </div>
                <button type="button" disabled={busy !== null} onClick={retryCheck} className={buttonClass}>
                    {busy === 'retry' ? 'Checking…' : 'Retry'}
                </button>
            </div>
        )
    }

    if (profiles.length === 0) return null

    const count = profiles.length

    return (
        <div className="rounded-lg border border-suspect-300 bg-suspect-50">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-suspect-200 px-4 py-3">
                <div className="flex items-start gap-3">
                    <ExclamationTriangleIcon
                        aria-hidden="true"
                        className="mt-1 h-5 w-5 shrink-0 text-suspect-700"
                    />
                    <div>
                        <p className="text-[10.5px] font-semibold uppercase tracking-wide text-suspect-700">
                            Needs attention
                        </p>
                        <p className="mt-0.5 text-[13px] font-semibold text-ink-900">
                            Applied to {count} {count === 1 ? 'profile' : 'profiles'} with LLM
                            classification switched off
                        </p>
                        <p className="mt-0.5 text-xs text-ink-600">
                            The prompt won&rsquo;t run for{' '}
                            {count === 1 ? 'this profile' : 'these profiles'} until the switch is
                            on. Turn it on here, or from the profile&rsquo;s settings page.
                        </p>
                    </div>
                </div>
                {count > 1 && (
                    <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() => turnOn(profiles, 'all')}
                        className={buttonClass}
                    >
                        {busy === 'all' ? 'Turning on…' : `Turn on for all ${count}`}
                    </button>
                )}
            </div>
            {error && (
                <p role="alert" className="px-4 pt-3 text-sm text-severe">
                    {error}
                </p>
            )}
            <ul className="divide-y divide-suspect-200">
                {profiles.map((p) => (
                    <li
                        key={p.id}
                        className="flex items-center justify-between gap-4 px-4 py-2.5 text-sm"
                    >
                        <div className="min-w-0">
                            <Link
                                to={`/account/${p.id}/settings`}
                                className="font-semibold text-arwen-700 hover:underline"
                            >
                                {labelOf(p)}
                            </Link>
                            {p.teamId != null && (
                                <span className="ml-2 text-xs text-ink-500">
                                    {p.teamName ?? `Team ${p.teamId}`}
                                </span>
                            )}
                        </div>
                        <button
                            type="button"
                            disabled={busy !== null}
                            onClick={() => turnOn([p], p.id)}
                            className={buttonClass}
                        >
                            {busy === p.id ? 'Turning on…' : 'Turn on'}
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    )
}
